import { useState } from "react";
import { useTranslations } from "use-intl";
import { useParams } from "@/lib/next-shims/next-navigation";
import {
  Activity,
  Webhook,
  Workflow as WorkflowIcon,
  History,
  Settings,
  ShieldCheck,
} from "lucide-react";
// ShieldCheck used in TABS array icon definition below
import { toast } from "sonner";
import { showError } from "@/lib/toast";
import { useWorkflow } from "@/hooks/api/queries/workflows.queries";
import { useRunWorkflow, useUpdateWorkflow } from "@/hooks/api/mutations/workflows.mutations";
import { TabButton } from "@/components/ui/tabs/TabWithUnderline";
import WorkflowHeader from "./components/WorkflowHeader";
import OverviewTab from "./components/tabs/OverviewTab";
import RunsTab from "./components/tabs/RunsTab";
import SettingsTab from "./components/tabs/SettingsTab";
import EditWorkflowModal from "./components/modals/EditWorkflowModal";
import RunConfirmModal from "./components/modals/RunConfirmModal";
import PermissionsTab from "./components/tabs/permissions-tab";
import ActivityTab from "./components/tabs/activity-tab";
import WebhooksTab from "./components/tabs/WebhooksTab";
import { useFeatureEnabled } from "@/features/features";
import { workflowRequiresAgenticRuntime } from "./lib/workflow-kind";

type TabId = "overview" | "runs" | "activity" | "webhooks" | "settings" | "permissions";

export default function WorkflowDetailClient() {
  const t = useTranslations("workflows");
  const params = useParams();
  const workflowId = params.id as string;

  const { data, isLoading, error } = useWorkflow(workflowId);
  const workflow = data?.data;
  const agentsEnabled = useFeatureEnabled("agents");

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRunConfirmOpen, setIsRunConfirmOpen] = useState(false);

  const runMutation = useRunWorkflow();
  const updateMutation = useUpdateWorkflow();
  const runDisabledReason =
    workflow && !agentsEnabled && workflowRequiresAgenticRuntime(workflow)
      ? t("agentic_runtime.run_disabled")
      : null;

  const TABS = [
    { id: "overview" as const, label: t("tabs.overview"), icon: WorkflowIcon },
    { id: "runs" as const, label: t("tabs.runs"), icon: History },
    { id: "activity" as const, label: t("tabs.activity"), icon: Activity },
    { id: "webhooks" as const, label: t("tabs.webhooks"), icon: Webhook },
    { id: "settings" as const, label: t("tabs.settings"), icon: Settings },
    { id: "permissions" as const, label: t("tabs.permissions"), icon: ShieldCheck },
  ];

  const handleRun = async () => {
    if (!workflow) return;
    runMutation.mutate(workflow.id, {
      onSuccess: () => {
        toast.success(t("detail.run_success"));
        setIsRunConfirmOpen(false);
        setActiveTab("runs");
      },
      onError: (err) => {
        showError((err as { message: string }).message || t("detail.run_failed"));
      },
    });
  };

  const handleUpdate = (formData: { name?: string; description?: string }) => {
    if (!workflow) return;
    updateMutation.mutate(
      { id: workflow.id, data: formData },
      {
        onSuccess: () => {
          toast.success(t("detail.update_success"));
          setIsEditOpen(false);
        },
        onError: (err) => {
          showError((err as { message: string }).message || t("detail.update_failed"));
        },
      },
    );
  };

  const handleToggleEnabled = () => {
    if (!workflow) return;
    updateMutation.mutate(
      { id: workflow.id, data: { isEnabled: !workflow.isEnabled } },
      {
        onSuccess: () => {
          toast.success(
            workflow.isEnabled ? t("detail.disabled_success") : t("detail.enabled_success"),
          );
        },
        onError: (err) => {
          showError((err as { message?: string }).message || t("detail.update_failed"));
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="size-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="py-32 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {error
            ? (error as { message: string }).message || t("detail.loading_failed")
            : t("detail.not_found")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* WorkflowKindChip + CostBudgetChip slots are rendered inside WorkflowHeader.
          CostBudgetChip ships A5.5; slot is reserved via kind prop passthrough. */}
      <WorkflowHeader
        workflow={workflow}
        onRun={() => setIsRunConfirmOpen(true)}
        onEdit={() => setIsEditOpen(true)}
        onToggleEnabled={handleToggleEnabled}
        isRunning={runMutation.isPending}
        isToggling={updateMutation.isPending}
        runDisabledReason={runDisabledReason}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-2 overflow-x-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              id={tab.id}
              label={tab.label}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && <OverviewTab workflow={workflow} />}
      {activeTab === "runs" && <RunsTab workflow={workflow} />}
      {activeTab === "activity" && <ActivityTab workflow={workflow} />}
      {activeTab === "webhooks" && <WebhooksTab workflow={workflow} />}
      {activeTab === "settings" && <SettingsTab workflow={workflow} />}
      {activeTab === "permissions" && <PermissionsTab workflow={workflow} />}

      {/* Modals */}
      <EditWorkflowModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
        workflow={workflow}
      />

      <RunConfirmModal
        isOpen={isRunConfirmOpen}
        onClose={() => setIsRunConfirmOpen(false)}
        onConfirm={handleRun}
        isLoading={runMutation.isPending}
        workflow={workflow}
        disabledReason={runDisabledReason}
      />
    </div>
  );
}
